import { forwardRef, ReactNode, useMemo } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormControlProps,
  FormHelperText,
  Stack,
  styled,
  Switch as MuiSwitch,
  SwitchProps as MuiSwitchProps,
} from '@mui/material';
import classNames from 'classnames';
import { v4 } from 'uuid';

import { Hint } from '@components/base';

export type SwitchProps = Pick<MuiSwitchProps, 'checked' | 'size'> &
  Pick<FormControlLabelProps, 'onChange'> &
  Pick<FormControlProps, 'disabled' | 'error' | 'required'> & {
    helperText?: ReactNode;
    hint?: ReactNode;
    label: string;
  };

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-right: ${({ theme }) => theme.spacing(0.5)};
`;

const StyledFormHelperText = styled(FormHelperText)`
  margin-left: 47px;
  margin-right: 0;
  margin-top: ${({ theme }) => theme.spacing(-0.75)};

  &.size--small {
    margin-left: 29px;
    margin-top: 0;
  }
`;

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    { helperText, checked, onChange, hint, label, disabled, required, error, size = 'small' },
    ref
  ) => {
    const hintId = useMemo(() => v4(), []);

    return (
      <Stack alignItems='center' direction='row'>
        <FormControl disabled={disabled} error={error} required={required}>
          <StyledFormControlLabel
            control={
              <MuiSwitch aria-describedby={hintId} checked={checked} ref={ref} size={size} />
            }
            label={label}
            onChange={onChange}
          />

          {helperText && (
            <StyledFormHelperText className={classNames(`size--${size}`)}>
              {helperText}
            </StyledFormHelperText>
          )}
        </FormControl>
        {hint && <Hint icon='info' id={hintId} tooltip={hint} />}
      </Stack>
    );
  }
);
